"use client";

import { useMemo, useState } from "react";
import type { CommunityRow } from "@/app/property-taxes/_lib/data";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 0] as const; // 0 = all
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

type Props = {
  data: CommunityRow[];
};

type SortKey =
  | "name"
  | "ward"
  | "parcels"
  | "assessed"
  | "residential"
  | "non_residential"
  | "total";

const MILLIONS = 1_000_000;
const THOUSANDS = 1_000;

function fmtCAD(v: number): string {
  if (v === 0) return "—";
  if (v >= MILLIONS) return `$${(v / MILLIONS).toFixed(1)}M`;
  if (v >= THOUSANDS) return `$${(v / THOUSANDS).toFixed(0)}k`;
  return `$${v.toLocaleString("en-CA")}`;
}

function fmtParcels(v: number): string {
  return v.toLocaleString("en-CA");
}

function compare(a: CommunityRow, b: CommunityRow, key: SortKey): number {
  switch (key) {
    case "name":
      return a.comm_name.localeCompare(b.comm_name);
    case "ward":
      // Numeric wards before 'unallocated', then by name as tiebreak.
      if (a.ward_num === b.ward_num) return a.comm_name.localeCompare(b.comm_name);
      if (a.ward_num === "unallocated") return 1;
      if (b.ward_num === "unallocated") return -1;
      return Number(a.ward_num) - Number(b.ward_num);
    case "parcels":
      return a.parcel_count - b.parcel_count;
    case "assessed":
      return a.assessed_value.total - b.assessed_value.total;
    case "residential":
      return a.tax_revenue.residential.total - b.tax_revenue.residential.total;
    case "non_residential":
      return (
        a.tax_revenue.non_residential.total -
        b.tax_revenue.non_residential.total
      );
    case "total":
      return a.tax_revenue.total - b.tax_revenue.total;
  }
}

export function ByCommunityTable({ data }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [query, setQuery] = useState("");
  const [wardFilter, setWardFilter] = useState<string>("all");
  const [pageSize, setPageSize] = useState<PageSize>(25);
  const [page, setPage] = useState(1);

  const wards = useMemo(() => {
    const seen = new Set<string>();
    for (const row of data) seen.add(row.ward_num);
    const list = Array.from(seen);
    list.sort((a, b) => {
      if (a === "unallocated") return 1;
      if (b === "unallocated") return -1;
      return Number(a) - Number(b);
    });
    return list;
  }, [data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = data.filter((r) => {
      if (wardFilter !== "all" && r.ward_num !== wardFilter) return false;
      if (!q) return true;
      return (
        r.comm_name.toLowerCase().includes(q) ||
        r.comm_code.toLowerCase().includes(q)
      );
    });
    const sorted = [...filtered].sort((a, b) => compare(a, b, sortKey));
    if (sortDir === "desc") sorted.reverse();
    return sorted;
  }, [data, query, wardFilter, sortKey, sortDir]);

  const totalPages =
    pageSize === 0 ? 1 : Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = pageSize === 0 ? 0 : (safePage - 1) * pageSize;
  const end = pageSize === 0 ? rows.length : start + pageSize;
  const visibleRows = rows.slice(start, end);
  const showPagination = pageSize !== 0 && rows.length > pageSize;

  // Re-ordering or re-filtering the row set invalidates the current page
  // index, so each control that changes it also resets to page 1 — the
  // user always lands on the most relevant rows for what they just
  // clicked. Done in the handlers (not an effect) to avoid an extra
  // commit + paint of the wrong page.
  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Numeric columns default to descending; name/ward default ascending.
      setSortDir(key === "name" || key === "ward" ? "asc" : "desc");
    }
    setPage(1);
  }

  function sortIndicator(key: SortKey): string {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <label className="flex flex-col text-sm">
          <span className="cs-label mb-1">search</span>
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="community name or code"
            className="border border-rule-2 bg-surface px-3 py-1.5 rounded text-sm w-64"
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="cs-label mb-1">ward</span>
          <select
            value={wardFilter}
            onChange={(e) => {
              setWardFilter(e.target.value);
              setPage(1);
            }}
            className="border border-rule-2 bg-surface px-3 py-1.5 rounded text-sm"
          >
            <option value="all">all wards</option>
            {wards.map((w) => (
              <option key={w} value={w}>
                {w === "unallocated" ? "unallocated" : `Ward ${w}`}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="cs-label mb-1">show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) as PageSize);
              setPage(1);
            }}
            className="border border-rule-2 bg-surface px-3 py-1.5 rounded text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? "all" : `${n} rows`}
              </option>
            ))}
          </select>
        </label>
        <span className="cs-mono text-ink-3 text-sm ml-auto">
          {rows.length.toLocaleString("en-CA")} of{" "}
          {data.length.toLocaleString("en-CA")} communities
        </span>
      </div>

      <div className="overflow-x-auto border border-rule-2 rounded">
        <table className="w-full text-sm">
          <thead className="bg-bg-2 text-left">
            <tr>
              <Th onClick={() => toggleSort("name")} ind={sortIndicator("name")}>
                Community
              </Th>
              <Th onClick={() => toggleSort("ward")} ind={sortIndicator("ward")}>
                Ward
              </Th>
              <ThR
                onClick={() => toggleSort("parcels")}
                ind={sortIndicator("parcels")}
              >
                Parcels
              </ThR>
              <ThR
                onClick={() => toggleSort("assessed")}
                ind={sortIndicator("assessed")}
              >
                Assessed value
              </ThR>
              <ThR
                onClick={() => toggleSort("residential")}
                ind={sortIndicator("residential")}
              >
                Residential tax
              </ThR>
              <ThR
                onClick={() => toggleSort("non_residential")}
                ind={sortIndicator("non_residential")}
              >
                Non-residential tax
              </ThR>
              <ThR
                onClick={() => toggleSort("total")}
                ind={sortIndicator("total")}
              >
                Total tax
              </ThR>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.comm_code} className="border-t border-rule-2">
                <td className="px-3 py-1.5">
                  <span className="cs-mono text-ink-3 mr-2">{r.comm_code}</span>
                  {r.comm_name}
                </td>
                <td className="px-3 py-1.5 text-ink-2">
                  {r.ward_num === "unallocated" ? "—" : r.ward_num}
                </td>
                <td className="px-3 py-1.5 text-right cs-mono">
                  {fmtParcels(r.parcel_count)}
                </td>
                <td className="px-3 py-1.5 text-right cs-mono">
                  {fmtCAD(r.assessed_value.total)}
                </td>
                <td className="px-3 py-1.5 text-right cs-mono">
                  {fmtCAD(r.tax_revenue.residential.total)}
                </td>
                <td className="px-3 py-1.5 text-right cs-mono">
                  {fmtCAD(r.tax_revenue.non_residential.total)}
                </td>
                <td className="px-3 py-1.5 text-right cs-mono font-medium">
                  {fmtCAD(r.tax_revenue.total)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-6 text-center text-ink-3 italic"
                >
                  No communities match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-1">
          <span className="cs-mono text-xs text-ink-3">
            Showing {(start + 1).toLocaleString("en-CA")}–
            {Math.min(end, rows.length).toLocaleString("en-CA")} of{" "}
            {rows.length.toLocaleString("en-CA")}
          </span>
          <div className="flex items-center gap-2">
            <PageButton
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              ← Prev
            </PageButton>
            <span className="cs-mono text-xs text-ink-3 tabular-nums">
              Page {safePage} / {totalPages}
            </span>
            <PageButton
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              Next →
            </PageButton>
          </div>
        </div>
      )}
    </div>
  );
}

function PageButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="cs-mono text-xs border border-rule-2 bg-surface px-2.5 py-1 rounded hover:text-signal disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-ink-3"
    >
      {children}
    </button>
  );
}

function Th({
  children,
  onClick,
  ind,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ind: string;
}) {
  return (
    <th className="cs-label font-normal">
      <button
        type="button"
        onClick={onClick}
        className="w-full px-3 py-3 hover:text-signal text-left"
      >
        {children}
        <span aria-hidden>{ind}</span>
      </button>
    </th>
  );
}

function ThR({
  children,
  onClick,
  ind,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ind: string;
}) {
  return (
    <th className="cs-label font-normal text-right">
      <button
        type="button"
        onClick={onClick}
        className="w-full px-3 py-3 hover:text-signal text-right"
      >
        {children}
        <span aria-hidden>{ind}</span>
      </button>
    </th>
  );
}
